import React, { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Typography, styled } from "@mui/material";
import {
  BottomActionButton,
  ColoredComponent,
  FormBody,
  FormContentContainer,
  FormHeader,
  FullScreenLoader,
  IconOptions,
  SecondaryActionButton,
} from "@components";
import { useAuth, useDoctorContext } from "@contexts";
import { useEditDoctor } from "@hooks";

const BiometricWrapper = styled(Box)({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "space-between",
});

const BiometricContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "32px",
});

const BiometricTextContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  marginTop: "16px",
});

const BiometricText = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  fontWeight: 500,
  color: theme.palette.text.primary,
}));

const BiometricIconsContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  gap: "16px",
});

const BiometricDisclaimer = styled(Typography)({
  fontSize: "14px",
  color: "#0D3C61",
});

const ButtonsContainer = styled(Box)({
  display: "flex",
  width: "100%",
  justifyContent: "space-evenly",
  gap: "16px",
});

const dummyLocationState = {
  isNewAccount: false,
  redirectTo: null,
};

export const BiometricRegistration: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isNewAccount, redirectTo } = location.state || dummyLocationState;
  const comeFromSignRecipe =
    redirectTo?.url.startsWith("/app/solicitudes") ?? false;

  const doctor = useDoctorContext();
  const { mutate: editDoctor } = useEditDoctor({
    code: doctor.code,
  });

  const {
    authChallengeFeedback: { message: authFeedback },
    biometricSignUp,
  } = useAuth();

  const biometricRedirectResolver = useCallback(() => {
    if (redirectTo) {
      navigate(redirectTo.url, { state: redirectTo.state });
    } else {
      navigate(
        isNewAccount ? "/app/verificacion-identidad" : "/app/solicitudes",
      );
    }
  }, [isNewAccount, navigate, redirectTo]);

  const onBiometricSignUp = useCallback(async () => {
    const signUpSuccess = await biometricSignUp(doctor);
    if (signUpSuccess) {
      editDoctor({
        showBiometricSecondFactor: false,
        showBiometricRegistration: false,
        biometricIsEnabled: true,
      });
    }

    return biometricRedirectResolver();
  }, [doctor, biometricSignUp, editDoctor, biometricRedirectResolver]);

  const cancelBiometricSignUp = useCallback(() => {
    if (comeFromSignRecipe) {
      editDoctor({
        showBiometricSecondFactor: false,
      });
    } else {
      editDoctor({
        showBiometricRegistration: false,
      });
    }

    return biometricRedirectResolver();
  }, [editDoctor, biometricRedirectResolver, comeFromSignRecipe]);

  const message = comeFromSignRecipe
    ? "¿Quieres activar tu huella o Face ID para firmar la próxima vez?"
    : "¿Quieres activar tu huella o Face ID para entrar la próxima vez?";

  if (authFeedback) {
    return (
      <>
        <FormHeader useMekiDocIcon />
        <FormContentContainer flex={1} disableGutters>
          <FormBody justifyContent="center">
            <FullScreenLoader title={authFeedback} />
          </FormBody>
        </FormContentContainer>
      </>
    );
  }

  return (
    <>
      <FormHeader useMekiDocIcon />
      <FormContentContainer disableGutters>
        <FormBody>
          <BiometricWrapper>
            <BiometricContainer>
              <BiometricTextContainer>
                <BiometricText>{message}</BiometricText>
              </BiometricTextContainer>

              <BiometricIconsContainer>
                <img
                  src="/assets/images/biometric-finger-print.svg"
                  alt="huella"
                />
                <img src="/assets/images/biometric-face-id.svg" alt="faceID" />
              </BiometricIconsContainer>

              <ColoredComponent
                color="#E9F5FE"
                icon={IconOptions.STYLED_INFO}
                text={
                  <BiometricDisclaimer>
                    Todas las huellas registradas en tu dispositivo tendrán
                    acceso a la aplicación
                  </BiometricDisclaimer>
                }
              />
            </BiometricContainer>
            <ButtonsContainer>
              <SecondaryActionButton
                onClick={cancelBiometricSignUp}
                sx={{ minWidth: "auto" }}
              >
                No gracias
              </SecondaryActionButton>
              <BottomActionButton
                onClick={onBiometricSignUp}
                size="small"
                sx={{ minWidth: "auto" }}
              >
                Activar
              </BottomActionButton>
            </ButtonsContainer>
          </BiometricWrapper>
        </FormBody>
      </FormContentContainer>
    </>
  );
};
